import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { cartService } from '@/services'
import { cartLineBusinessKey } from '@/utils/cartLineIdentity'
import {
  writeRegisterPendingInvoicePaymentSession,
  clearRegisterPendingInvoicePaymentSession
} from '@/utils/registerPendingInvoicePaymentSession'
import { useLocationStore } from '@/stores/location'
import { useRegisterStore } from '@/stores/register'

const CART_BATCH_LINE_OPS = new Set(['add_item', 'set_quantity', 'remove_item', 'clear_cart'])

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref([])
  const loading = ref(false)
  const error = ref(null)
  const lastUpdated = ref(null)
  const pendingTabPayment = ref(null)
  const pendingTabCredit = ref(null)
  const pendingInvoicePayment = ref(null)
  const customerId = ref(null)
  /** @type {import('vue').Ref<object|null>} */
  const serverSummary = ref(null)
  /** @type {import('vue').Ref<string|null>} */
  const orderDiscountType = ref(null)
  /** @type {import('vue').Ref<number|null>} */
  const orderDiscountAmount = ref(null)
  /** Monotonic server cart revision from last API payload (for batch base_cart_revision). */
  const cartRevision = ref(null)
  /** Set after resume; cleared when the cart is empty or explicitly cleared server-side. */
  const activeSuspendedSaleId = ref(null)

  let fetchCartGeneration = 0

  // Getters
  const itemCount = computed(() => items.value.length)

  const totalQuantity = computed(() =>
    items.value.reduce((total, item) => total + (item.quantity || 0), 0)
  )

  const subtotal = computed(() =>
    items.value.reduce((total, item) => {
      const itemTotal = (item.price || 0) * (item.quantity || 0)
      return total + itemTotal
    }, 0)
  )

  const hasItems = computed(() => items.value.length > 0)

  const hasPendingTabPayment = computed(() => !!pendingTabPayment.value)

  const hasPendingTabCredit = computed(() => !!pendingTabCredit.value)

  const hasPendingTabCheckout = computed(() => !!pendingTabPayment.value || !!pendingTabCredit.value)

  const hasPendingInvoicePayment = computed(() => !!pendingInvoicePayment.value)

  const hasPendingRegisterCheckout = computed(
    () => hasPendingTabCheckout.value || hasPendingInvoicePayment.value
  )

  const pendingInvoicePaymentSessionScope = () => {
    try {
      const locationStore = useLocationStore()
      const registerStore = useRegisterStore()
      return {
        locationId: locationStore.getCurrentLocation?.id ?? null,
        registerId: registerStore.currentRegister?.id ?? null
      }
    } catch {
      return {}
    }
  }

  const clearPendingInvoicePaymentState = () => {
    pendingInvoicePayment.value = null
    clearRegisterPendingInvoicePaymentSession()
  }

  const batchClearsPendingRegisterCheckout = (ops = []) =>
    (ops || []).some((entry) => CART_BATCH_LINE_OPS.has(entry?.op))

  /**
   * Reconcile server cart lines into existing item objects where line_item_id matches
   * to reduce list thrashing / layout jumps in the register UI.
   *
   * @param {Array} prevItems
   * @param {Array} nextItems
   * @returns {Array}
   */
  const mergeCartItemsFromServer = (prevItems, nextItems) => {
    const prevById = new Map(
      (prevItems || []).map((it) => [it.line_item_id, it])
    )
    const merged = []
    for (const s of nextItems || []) {
      const id = s.line_item_id
      const existing = prevById.get(id)
      if (existing) {
        existing.quantity = s.quantity
        existing.price = s.price
        existing.name = s.name
        existing.sku = s.sku
        if (s.product_id != null) {
          existing.product_id = s.product_id
        }
        existing.metadata = { ...(s.metadata || {}) }
        if (Object.prototype.hasOwnProperty.call(s, 'redeeming_for_points')) {
          existing.redeeming_for_points = s.redeeming_for_points
        }
        merged.push(existing)
      } else {
        merged.push({
          ...s,
          metadata: { ...(s.metadata || {}) }
        })
      }
    }

    const mergedKeys = new Set(merged.map((it) => cartLineBusinessKey(it)))
    for (const p of prevItems || []) {
      const meta = p.metadata || {}
      if (!meta.__registerCartPendingAdd) continue
      const k = cartLineBusinessKey(p)
      if (mergedKeys.has(k)) continue
      merged.push(p)
      mergedKeys.add(k)
    }

    return merged
  }

  /**
   * @param {object} cartData
   * @param {{ mergeItems?: boolean }} [options]
   */
  const applyCartServerPayload = (cartData, options = {}) => {
    if (!cartData) {
      return
    }
    const mergeItems = options.mergeItems === true
    const nextItems = cartData.items || []

    if (mergeItems && items.value.length > 0) {
      items.value = mergeCartItemsFromServer(items.value, nextItems)
    } else {
      items.value = nextItems
    }

    customerId.value = cartData.customer_id ?? null
    lastUpdated.value = cartData.updated_at ? new Date(cartData.updated_at) : new Date()
    serverSummary.value = cartData.summary || null
    if (cartData.discount_type != null && cartData.discount_type !== '') {
      orderDiscountType.value = cartData.discount_type
      orderDiscountAmount.value = Number(cartData.discount_amount) || 0
    } else {
      orderDiscountType.value = null
      orderDiscountAmount.value = null
    }
    if (Object.prototype.hasOwnProperty.call(cartData, 'cart_revision') && cartData.cart_revision != null) {
      cartRevision.value = Number(cartData.cart_revision) || 0
    }
    if (Object.prototype.hasOwnProperty.call(cartData, 'active_suspended_sale_id')) {
      const sid = cartData.active_suspended_sale_id
      activeSuspendedSaleId.value = sid != null && sid !== '' ? Number(sid) || null : null
    } else if (items.value.length === 0) {
      activeSuspendedSaleId.value = null
    }
  }

  // Actions
  const fetchCart = async () => {
    loading.value = true
    error.value = null
    const gen = ++fetchCartGeneration

    try {
      const cartData = await cartService.getCart()
      if (gen !== fetchCartGeneration) {
        return cartData
      }
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to fetch cart'
      console.error('Error fetching cart:', err)
      throw err
    } finally {
      if (gen === fetchCartGeneration) {
        loading.value = false
      }
    }
  }

  /**
   * Apply a transactional batch of cart ops (one HTTP request, idempotent).
   * @param {{ ops: Array<{ op: string, payload: object }> }} payload
   * @param {string} idempotencyKey
   */
  const applyCartBatch = async (payload, idempotencyKey) => {
    error.value = null

    try {
      if (batchClearsPendingRegisterCheckout(payload?.ops)) {
        if (pendingTabPayment.value) {
          pendingTabPayment.value = null
        }
        if (pendingTabCredit.value) {
          pendingTabCredit.value = null
        }
        if (pendingInvoicePayment.value) {
          clearPendingInvoicePaymentState()
        }
      }
      const body = {
        ops: payload.ops
      }
      if (cartRevision.value != null && Number.isFinite(cartRevision.value)) {
        body.base_cart_revision = cartRevision.value
      }
      const cartData = await cartService.applyBatch(body, idempotencyKey)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to apply cart batch'
      console.error('Error applying cart batch:', err)
      throw err
    }
  }

  const addItem = async (product, quantity = 1, options = {}) => {
    error.value = null

    try {
      if (pendingTabPayment.value) {
        pendingTabPayment.value = null
      }
      if (pendingTabCredit.value) {
        pendingTabCredit.value = null
      }
      if (pendingInvoicePayment.value) {
        clearPendingInvoicePaymentState()
      }
      const cartData = await cartService.addItem(product, quantity, options)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to add item to cart'
      console.error('Error adding item to cart:', err)
      throw err
    }
  }

  const updateQuantity = async (lineItemId, quantity, metadata = undefined) => {
    error.value = null

    try {
      const cartData = await cartService.setQuantity(lineItemId, quantity, metadata)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to update item quantity'
      console.error('Error updating item quantity:', err)
      throw err
    }
  }

  const removeItem = async (lineItemId) => {
    error.value = null

    try {
      const cartData = await cartService.removeItem(lineItemId)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to remove item from cart'
      console.error('Error removing item from cart:', err)
      throw err
    }
  }

  const clearCart = async () => {
    error.value = null

    try {
      const res = await cartService.clearCart()
      items.value = []
      customerId.value = null
      lastUpdated.value = new Date()
      serverSummary.value = res?.summary || null
      orderDiscountType.value = null
      orderDiscountAmount.value = null
      if (Object.prototype.hasOwnProperty.call(res || {}, 'cart_revision') && res?.cart_revision != null) {
        cartRevision.value = Number(res.cart_revision) || 0
      } else {
        cartRevision.value = 0
      }
      activeSuspendedSaleId.value = null
    } catch (err) {
      error.value = err.message || 'Failed to clear cart'
      console.error('Error clearing cart:', err)
      throw err
    }
  }

  /**
   * @deprecated Buggy (passes sku as line id). Unused - prefer `cartBatchOp` + coordinator.
   */
  const updateMultipleItems = async (updates) => {
    error.value = null

    try {
      const cartData = await cartService.updateMultipleItems(updates)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to update multiple items'
      console.error('Error updating multiple items:', err)
      throw err
    }
  }

  const reset = () => {
    items.value = []
    loading.value = false
    error.value = null
    lastUpdated.value = null
    pendingTabPayment.value = null
    pendingTabCredit.value = null
    clearPendingInvoicePaymentState()
    customerId.value = null
    serverSummary.value = null
    orderDiscountType.value = null
    orderDiscountAmount.value = null
    cartRevision.value = null
    activeSuspendedSaleId.value = null
    fetchCartGeneration++
  }

  const setPendingTabPayment = (context) => {
    pendingTabPayment.value = context || null
    if (context) {
      pendingTabCredit.value = null
      clearPendingInvoicePaymentState()
    }
  }

  const clearPendingTabPayment = () => {
    pendingTabPayment.value = null
  }

  const setPendingTabCredit = (context) => {
    pendingTabCredit.value = context || null
    if (context) {
      pendingTabPayment.value = null
      clearPendingInvoicePaymentState()
    }
  }

  const clearPendingTabCredit = () => {
    pendingTabCredit.value = null
  }

  const setPendingInvoicePayment = (context) => {
    pendingInvoicePayment.value = context || null
    if (context) {
      pendingTabPayment.value = null
      pendingTabCredit.value = null
      writeRegisterPendingInvoicePaymentSession(context, pendingInvoicePaymentSessionScope())
    } else {
      clearRegisterPendingInvoicePaymentSession()
    }
  }

  const restorePendingInvoicePayment = (context) => {
    pendingInvoicePayment.value = context || null
    if (context) {
      pendingTabPayment.value = null
      pendingTabCredit.value = null
    }
  }

  const clearPendingInvoicePayment = () => {
    clearPendingInvoicePaymentState()
  }

  const setCartCustomerId = async (id) => {
    error.value = null

    try {
      const cartData = await cartService.setCustomer(id)
      applyCartServerPayload(cartData, { mergeItems: true })
      return cartData
    } catch (err) {
      error.value = err.message || 'Failed to set cart customer'
      console.error('Error setting cart customer:', err)
      throw err
    }
  }

  return {
    // State
    items,
    loading,
    error,
    lastUpdated,
    pendingTabPayment,
    pendingTabCredit,
    pendingInvoicePayment,
    customerId,
    serverSummary,
    orderDiscountType,
    orderDiscountAmount,
    cartRevision,
    activeSuspendedSaleId,

    // Getters
    itemCount,
    totalQuantity,
    subtotal,
    hasItems,
    hasPendingTabPayment,
    hasPendingTabCredit,
    hasPendingTabCheckout,
    hasPendingInvoicePayment,
    hasPendingRegisterCheckout,

    // Actions
    fetchCart,
    /** Apply a full cart JSON from the API (e.g. suspend/resume) without an extra GET. */
    applyCartServerPayload,
    applyCartBatch,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    updateMultipleItems,

    // Utility
    reset,
    setPendingTabPayment,
    clearPendingTabPayment,
    setPendingTabCredit,
    clearPendingTabCredit,
    setPendingInvoicePayment,
    restorePendingInvoicePayment,
    clearPendingInvoicePayment,
    setCartCustomerId
  }
})
